import React, { useRef, useMemo, useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';

const DailyReportModal = ({ payments, families, startDate, endDate, onClose, apiCall }) => {
  const reportRef = useRef(null);
  const [allocationsMap, setAllocationsMap] = useState({}); // paymentID → allocations[]
  const [storeReceipts, setStoreReceipts] = useState([]);   // uniforms/books cash sales
  const [storeBreakdown, setStoreBreakdown] = useState([]);  // by category (Clothes/Books/…)

  // Store sales live outside the Payments table (standalone cash sale), so pull
  // them for the same period. Fails silently if the user lacks store access.
  useEffect(() => {
    if (!apiCall || !startDate || !endDate) return;
    apiCall(`/store/day-report?from=${startDate}&to=${endDate}`)
      .then((d) => {
        setStoreReceipts(Array.isArray(d?.receipts) ? d.receipts : []);
        setStoreBreakdown(Array.isArray(d?.categoryBreakdown) ? d.categoryBreakdown : []);
      })
      .catch(() => { setStoreReceipts([]); setStoreBreakdown([]); });
  }, [apiCall, startDate, endDate]);

  // Fetch allocations for art school (month/class breakdown) and activities
  // (per-activity breakdown) payments
  useEffect(() => {
    const allocPayments = payments.filter(p => p.Module === 'ArtSchool' || p.Module === 'Activities');
    if (!allocPayments.length || !apiCall) return;

    Promise.all(
      allocPayments.map(p =>
        apiCall(`/payments/${p.PaymentID}/allocations`, { method: 'GET' })
          .then(data => ({ id: p.PaymentID, allocations: data || [] }))
          .catch(() => ({ id: p.PaymentID, allocations: [] }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => { map[r.id] = r.allocations; });
      setAllocationsMap(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  const getFamilyName = (familyId) => {
    const family = families.find(f => f.FamilyID === familyId);
    return family ? family.FatherName : familyId;
  };

  const afterschoolPayments = payments.filter(p => p.Module === 'Afterschool');
  const busPayments = payments.filter(p => p.Module === 'Bus');
  const activitiesPayments = payments.filter(p => p.Module === 'Activities');
  const daycarePayments = payments.filter(p => p.Module === 'Daycare');
  const busRentalPayments = payments.filter(p => p.Module === 'BusRental');
  const artSchoolPayments = payments.filter(p => p.Module === 'ArtSchool');
  
  const afterschoolTotal = afterschoolPayments.reduce((sum, p) => sum + p.Amount, 0);
  const busTotal = busPayments.reduce((sum, p) => sum + p.Amount, 0);
  const activitiesTotal = activitiesPayments.reduce((sum, p) => sum + p.Amount, 0);
  const daycareTotal = daycarePayments.reduce((sum, p) => sum + p.Amount, 0);
  const busRentalTotal = busRentalPayments.reduce((sum, p) => sum + p.Amount, 0);
  const artSchoolTotal = artSchoolPayments.reduce((sum, p) => sum + p.Amount, 0);
  const storeTotal = storeReceipts.reduce((sum, r) => sum + Number(r.AmountPaid || 0), 0);
  const grandTotal = afterschoolTotal + busTotal + activitiesTotal + daycareTotal + busRentalTotal + artSchoolTotal + storeTotal;

  // Activity breakdown from PaymentAllocations: one line per activity, summed
  // across payments, so a payment covering several activities is split correctly.
  const activityBreakdown = useMemo(() => {
    const breakdown = {};

    activitiesPayments.forEach(payment => {
      const allocations = allocationsMap[payment.PaymentID];

      if (allocations && allocations.length > 0) {
        allocations.forEach(alloc => {
          const name = alloc.ActivityName || alloc.Description || 'General Activities';
          if (!breakdown[name]) breakdown[name] = 0;
          breakdown[name] += parseFloat(alloc.Amount || 0);
        });
      } else {
        // Fallback while allocations load, or for old payments without allocation rows
        const name = payment.Notes || 'General Activities';
        if (!breakdown[name]) breakdown[name] = 0;
        breakdown[name] += payment.Amount;
      }
    });

    return Object.entries(breakdown)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);
  }, [activitiesPayments, allocationsMap]);

  // ✅ Calculate Daycare month breakdown from Notes field
  const daycareMonthBreakdown = useMemo(() => {
    const breakdown = {};
    
    daycarePayments.forEach(payment => {
      // Extract month/year from Notes
      // Expected format: "January 2026" or "January 2026, February 2026"
      const monthsText = payment.Notes || 'Unspecified';
      
      if (monthsText.includes(',')) {
        // Multi-month payment
        const months = monthsText.split(',').map(m => m.trim());
        const amountPerMonth = payment.Amount / months.length;
        months.forEach(month => {
          if (!breakdown[month]) {
            breakdown[month] = 0;
          }
          breakdown[month] += amountPerMonth;
        });
      } else {
        // Single month payment
        if (!breakdown[monthsText]) {
          breakdown[monthsText] = 0;
        }
        breakdown[monthsText] += payment.Amount;
      }
    });
    
    // Convert to array and sort chronologically
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    return Object.entries(breakdown)
      .map(([month, total]) => ({ month, total }))
      .sort((a, b) => {
        const aMonthIndex = monthOrder.findIndex(m => a.month.includes(m));
        const bMonthIndex = monthOrder.findIndex(m => b.month.includes(m));
        if (aMonthIndex === -1) return 1;
        if (bMonthIndex === -1) return -1;
        return aMonthIndex - bMonthIndex;
      });
  }, [daycarePayments]);

  // Art School breakdown from PaymentAllocations → MonthlyCharges.Description
  // Description format: "Drums (Private) - May 2026" or "Drums (Private) - May 2026 (3-month block)"
  const artSchoolBreakdown = useMemo(() => {
    const breakdown = {};

    artSchoolPayments.forEach(payment => {
      const allocations = allocationsMap[payment.PaymentID];

      if (allocations && allocations.length > 0) {
        // Use actual allocation descriptions (most accurate)
        allocations.forEach(alloc => {
          const desc = alloc.Description || 'Art School';
          if (!breakdown[desc]) breakdown[desc] = 0;
          breakdown[desc] += parseFloat(alloc.Amount || 0);
        });
      } else {
        // Fallback to Notes field while allocations are loading
        const classText = payment.Notes || 'Art School';
        const classes = classText.split(',').map(c => c.trim());
        const amountPerClass = payment.Amount / classes.length;
        classes.forEach(cls => {
          if (!breakdown[cls]) breakdown[cls] = 0;
          breakdown[cls] += amountPerClass;
        });
      }
    });

    return Object.entries(breakdown)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [artSchoolPayments, allocationsMap]);

  const handlePrint = () => {
    const windowPrint = window.open('', '', 'width=900,height=700');
    
    // Generate Activities section HTML
    const activitiesPaymentsHTML = activitiesPayments.length > 0 ? `
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;">
        <thead>
          <tr style="background-color: #f9fafb; border-bottom: 1px solid #e5e7eb;">
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Date</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Receipt #</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Family</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Method</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #374151;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${activitiesPayments.map(payment => `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">#${payment.PaymentID}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.FamilyID} - ${getFamilyName(payment.FamilyID)}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.PaymentMethod}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color: #fff7ed; border-top: 2px solid #fdba74;">
            <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Activities Total:</td>
            <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #ea580c;">$${activitiesTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      
      ${activityBreakdown.length > 0 ? `
        <div style="margin-top: 16px; padding: 12px; background-color: #fffbeb; border: 1px solid #fbbf24; border-radius: 6px;">
          <h4 style="font-size: 12px; font-weight: 600; color: #92400e; margin-bottom: 8px;">Activity Breakdown:</h4>
          <ul style="list-style: none; padding: 0; margin: 0;">
            ${activityBreakdown.map(activity => `
              <li style="font-size: 11px; padding: 4px 0; display: flex; justify-content: space-between; border-bottom: 1px dotted #fbbf24;">
                <span style="color: #78716c;">${activity.name}</span>
                <span style="font-weight: 600; color: #92400e;">$${activity.total.toFixed(2)}</span>
              </li>
            `).join('')}
          </ul>
        </div>
      ` : ''}
    ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No activities payments in this period</p>';
    
    // ✅ Generate Daycare section HTML
    const daycarePaymentsHTML = daycarePayments.length > 0 ? `
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;">
        <thead>
          <tr style="background-color: #f9fafb; border-bottom: 1px solid #e5e7eb;">
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Date</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Receipt #</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Family</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Method</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #374151;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${daycarePayments.map(payment => `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">#${payment.PaymentID}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.FamilyID} - ${getFamilyName(payment.FamilyID)}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.PaymentMethod}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color: #faf5ff; border-top: 2px solid #c084fc;">
            <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Daycare Total:</td>
            <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #7c3aed;">$${daycareTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      
      ${daycareMonthBreakdown.length > 0 ? `
        <div style="margin-top: 16px; padding: 12px; background-color: #faf5ff; border: 1px solid #c084fc; border-radius: 6px;">
          <h4 style="font-size: 12px; font-weight: 600; color: #6b21a8; margin-bottom: 8px;">Month Breakdown:</h4>
          <ul style="list-style: none; padding: 0; margin: 0;">
            ${daycareMonthBreakdown.map(item => `
              <li style="font-size: 11px; padding: 4px 0; display: flex; justify-content: space-between; border-bottom: 1px dotted #c084fc;">
                <span style="color: #78716c;">${item.month}</span>
                <span style="font-weight: 600; color: #6b21a8;">$${item.total.toFixed(2)}</span>
              </li>
            `).join('')}
          </ul>
        </div>
      ` : ''}
    ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No daycare payments in this period</p>';
    
    // Bus Rental payments HTML
    const busRentalPaymentsHTML = busRentalPayments.length > 0 ? `
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;">
        <thead>
          <tr style="background-color: #f9fafb; border-bottom: 1px solid #e5e7eb;">
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Date</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Receipt #</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Group/Destination</th>
            <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Method</th>
            <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #374151;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${busRentalPayments.map(payment => `
            <tr style="border-bottom: 1px solid #e5e7eb;">
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">#${payment.PaymentID}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.Notes || payment.ReferenceNumber}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.PaymentMethod}</td>
              <td style="padding: 8px 12px; font-size: 12px; color: #1f2937; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color: #f3e8ff; border-top: 2px solid #c084fc;">
            <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Bus Rental Total:</td>
            <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #7c3aed;">$${busRentalTotal.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
    ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No bus rental payments in this period</p>';
    
    windowPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Payment Report ${startDate} - ${endDate}</title>
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; background: white; }
            @media print { body { padding: 10px; } @page { size: A4; margin: 1cm; } }
          </style>
        </head>
        <body>
          <!-- Header -->
          <div style="text-align:center;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #1d4ed8;">
            <h1 style="font-size:26px;font-weight:700;color:#1f2937;margin-bottom:4px;">Collège des Apôtres</h1>
            <h2 style="font-size:16px;font-weight:600;color:#1d4ed8;margin-bottom:4px;">Daily Payment Report</h2>
            <p style="font-size:13px;color:#6b7280;">
              ${startDate === endDate
                ? `Period: ${new Date(startDate).toLocaleDateString()}`
                : `Period: ${new Date(startDate).toLocaleDateString()} — ${new Date(endDate).toLocaleDateString()}`
              }
            </p>
            <p style="font-size:11px;color:#9ca3af;margin-top:4px;">Generated: ${new Date().toLocaleString()}</p>
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #d1fae5; padding: 8px 16px; border-radius: 4px;">Afterschool Payments</h3>
            ${afterschoolPayments.length > 0 ? `
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="background-color: #f9fafb; border-bottom: 2px solid #e5e7eb;">
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Date</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Receipt #</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Family</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Method</th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${afterschoolPayments.map(payment => `
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <td style="padding: 8px 12px; font-size: 12px;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">#${payment.PaymentID}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">${payment.FamilyID} - ${getFamilyName(payment.FamilyID)}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">${payment.PaymentMethod}</td>
                      <td style="padding: 8px 12px; font-size: 12px; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
                <tfoot>
                  <tr style="background-color: #d1fae5; border-top: 2px solid #34d399;">
                    <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Afterschool Total:</td>
                    <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #059669;">$${afterschoolTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No afterschool payments in this period</p>'}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #dbeafe; padding: 8px 16px; border-radius: 4px;">Bus Payments</h3>
            ${busPayments.length > 0 ? `
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="background-color: #f9fafb; border-bottom: 2px solid #e5e7eb;">
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Date</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Receipt #</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Family</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Method</th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${busPayments.map(payment => `
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <td style="padding: 8px 12px; font-size: 12px;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">#${payment.PaymentID}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">${payment.FamilyID} - ${getFamilyName(payment.FamilyID)}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">${payment.PaymentMethod}</td>
                      <td style="padding: 8px 12px; font-size: 12px; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
                <tfoot>
                  <tr style="background-color: #dbeafe; border-top: 2px solid #60a5fa;">
                    <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Bus Total:</td>
                    <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #2563eb;">$${busTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No bus payments in this period</p>'}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #fed7aa; padding: 8px 16px; border-radius: 4px;">Activities Payments</h3>
            ${activitiesPaymentsHTML}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #e9d5ff; padding: 8px 16px; border-radius: 4px;">Daycare Payments</h3>
            ${daycarePaymentsHTML}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #e0e7ff; padding: 8px 16px; border-radius: 4px;">Art School Payments</h3>
            ${artSchoolPayments.length > 0 ? `
              <table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;">
                <thead>
                  <tr style="background-color: #f9fafb; border-bottom: 1px solid #e5e7eb;">
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Date</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Receipt #</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Family</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151;">Method</th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #374151;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${artSchoolPayments.map(payment => `
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${new Date(payment.PaymentDate).toLocaleDateString()}</td>
                      <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">#${payment.PaymentID}</td>
                      <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.FamilyID} - ${getFamilyName(payment.FamilyID)}</td>
                      <td style="padding: 8px 12px; font-size: 12px; color: #1f2937;">${payment.PaymentMethod}</td>
                      <td style="padding: 8px 12px; font-size: 12px; color: #1f2937; text-align: right; font-weight: 600;">$${payment.Amount.toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
                <tfoot>
                  <tr style="background-color: #e0e7ff; border-top: 2px solid #818cf8;">
                    <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Art School Total:</td>
                    <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #4338ca;">$${artSchoolTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
              ${artSchoolBreakdown.length > 0 ? `
                <div style="margin-top: 16px; padding: 12px; background-color: #e0e7ff; border: 1px solid #818cf8; border-radius: 6px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #3730a3; margin-bottom: 8px;">Class Breakdown:</h4>
                  <ul style="list-style: none; padding: 0; margin: 0;">
                    ${artSchoolBreakdown.map(item => `
                      <li style="font-size: 11px; padding: 4px 0; display: flex; justify-content: space-between; border-bottom: 1px dotted #818cf8;">
                        <span style="color: #4b5563;">${item.name}</span>
                        <span style="font-weight: 600; color: #3730a3;">$${item.total.toFixed(2)}</span>
                      </li>
                    `).join('')}
                  </ul>
                </div>
              ` : ''}
            ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No art school payments in this period</p>'}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #f3e8ff; padding: 8px 16px; border-radius: 4px;">Bus Rental Payments</h3>
            ${busRentalPaymentsHTML}
          </div>

          <div style="margin-bottom: 30px;">
            <h3 style="font-size: 16px; font-weight: 700; margin: 0 0 12px 0; background-color: #ccfbf1; padding: 8px 16px; border-radius: 4px;">Store (Uniforms &amp; Books)</h3>
            ${storeReceipts.length > 0 ? `
              <table style="width: 100%; border-collapse: collapse;">
                <thead>
                  <tr style="background-color: #f9fafb; border-bottom: 2px solid #e5e7eb;">
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Date</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Receipt #</th>
                    <th style="padding: 8px 12px; text-align: left; font-size: 12px; font-weight: 600;">Family</th>
                    <th style="padding: 8px 12px; text-align: center; font-size: 12px; font-weight: 600;">Items</th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 12px; font-weight: 600;">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${storeReceipts.map(r => `
                    <tr style="border-bottom: 1px solid #e5e7eb;">
                      <td style="padding: 8px 12px; font-size: 12px;">${r.PaidAt ? new Date(r.PaidAt).toLocaleDateString() : ''}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">#${r.ReceiptNumber}</td>
                      <td style="padding: 8px 12px; font-size: 12px;">${r.FamilyID} - ${r.FamilyName || r.FamilyID}</td>
                      <td style="padding: 8px 12px; font-size: 12px; text-align: center;">${r.ItemCount}</td>
                      <td style="padding: 8px 12px; font-size: 12px; text-align: right; font-weight: 600;">$${Number(r.AmountPaid).toFixed(2)}</td>
                    </tr>
                  `).join('')}
                </tbody>
                <tfoot>
                  <tr style="background-color: #ccfbf1; border-top: 2px solid #5eead4;">
                    <td colspan="4" style="padding: 8px 12px; text-align: right; font-weight: 600; font-size: 13px;">Store Total:</td>
                    <td style="padding: 8px 12px; text-align: right; font-weight: bold; font-size: 13px; color: #0d9488;">$${storeTotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
              ${storeBreakdown.length > 0 ? `
                <div style="margin-top: 16px; padding: 12px; background-color: #f0fdfa; border: 1px solid #5eead4; border-radius: 6px;">
                  <h4 style="font-size: 12px; font-weight: 600; color: #115e59; margin-bottom: 8px;">Category Breakdown:</h4>
                  <ul style="list-style: none; padding: 0; margin: 0;">
                    ${storeBreakdown.map(c => `
                      <li style="font-size: 11px; padding: 4px 0; display: flex; justify-content: space-between; border-bottom: 1px dotted #5eead4;">
                        <span style="color: #4b5563;">${c.category}</span>
                        <span style="font-weight: 600; color: #115e59;">$${Number(c.total).toFixed(2)}</span>
                      </li>
                    `).join('')}
                  </ul>
                </div>
              ` : ''}
            ` : '<p style="text-align: center; padding: 20px; color: #6b7280; font-style: italic;">No store sales in this period</p>'}
          </div>

          <!-- Summary -->
          <div style="margin-top:28px;page-break-inside:avoid;">
            <h3 style="font-size:15px;font-weight:700;margin:0 0 12px 0;background:#f1f5f9;padding:8px 14px;border-left:4px solid #1d4ed8;border-radius:0 4px 4px 0;color:#1e3a8a;">
              Summary
            </h3>
            <table style="width:300px;border-collapse:collapse;margin-left:auto;">
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Afterschool:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#059669;">$${afterschoolTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Bus:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#2563eb;">$${busTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Activities:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#ea580c;">$${activitiesTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Daycare:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#7c3aed;">$${daycareTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Art School:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#4338ca;">$${artSchoolTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Bus Rental:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#7c3aed;">$${busRentalTotal.toFixed(2)}</td>
              </tr>
              <tr style="border-bottom:1px solid #d1d5db;">
                <td style="padding:8px;font-size:13px;color:#374151;">Store:</td>
                <td style="padding:8px;font-size:13px;text-align:right;font-weight:600;color:#0d9488;">$${storeTotal.toFixed(2)}</td>
              </tr>
              <tr style="background:#1d4ed8;">
                <td style="padding:10px 8px;font-size:15px;font-weight:700;color:#fff;">GRAND TOTAL:</td>
                <td style="padding:10px 8px;font-size:15px;text-align:right;font-weight:700;color:#fff;">$${grandTotal.toFixed(2)}</td>
              </tr>
            </table>
          </div>

          <!-- Footer -->
          <div style="margin-top:40px;padding-top:12px;border-top:2px solid #e5e7eb;display:flex;justify-content:space-between;align-items:flex-end;">
            <div style="font-size:11px;color:#9ca3af;">Collège des Apôtres · Payment Department</div>
            <div style="text-align:right;">
              <div style="border-top:1px solid #374151;padding-top:6px;margin-top:36px;font-size:11px;font-weight:600;color:#374151;">Authorized Signature</div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    windowPrint.document.close();
    windowPrint.focus();
    setTimeout(() => {
      windowPrint.print();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Payment Report</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-5 h-5" />
              Print
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div ref={reportRef}>
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-900">Collège des Apôtres</h1>
              <h2 className="text-xl font-semibold text-gray-700 mt-2">Payment Report</h2>
              <p className="text-gray-600 mt-1">
                {startDate === endDate 
                  ? `Period: ${new Date(startDate).toLocaleDateString()}`
                  : `Period: ${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
                }
              </p>
            </div>

            {/* Afterschool Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-green-100 px-4 py-2 rounded">Afterschool Payments</h3>
              {afterschoolPayments.length > 0 ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {afterschoolPayments.map(payment => (
                      <tr key={payment.PaymentID} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                        <td className="px-4 py-2 text-sm">{payment.FamilyID} - {getFamilyName(payment.FamilyID)}</td>
                        <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-green-50 font-bold border-t-2 border-green-300">
                      <td colSpan="4" className="px-4 py-3 text-right">Afterschool Total:</td>
                      <td className="px-4 py-3 text-right text-green-700 text-lg">${afterschoolTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No afterschool payments in this period</p>
              )}
            </div>

            {/* Bus Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-blue-100 px-4 py-2 rounded">Bus Payments</h3>
              {busPayments.length > 0 ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busPayments.map(payment => (
                      <tr key={payment.PaymentID} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                        <td className="px-4 py-2 text-sm">{payment.FamilyID} - {getFamilyName(payment.FamilyID)}</td>
                        <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-blue-50 font-bold border-t-2 border-blue-300">
                      <td colSpan="4" className="px-4 py-3 text-right">Bus Total:</td>
                      <td className="px-4 py-3 text-right text-blue-700 text-lg">${busTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No bus payments in this period</p>
              )}
            </div>

            {/* Activities Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-orange-100 px-4 py-2 rounded">Activities Payments</h3>
              {activitiesPayments.length > 0 ? (
                <div>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activitiesPayments.map(payment => (
                        <tr key={payment.PaymentID} className="border-b border-gray-200">
                          <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                          <td className="px-4 py-2 text-sm">{payment.FamilyID} - {getFamilyName(payment.FamilyID)}</td>
                          <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-orange-50 font-bold border-t-2 border-orange-300">
                        <td colSpan="4" className="px-4 py-3 text-right">Activities Total:</td>
                        <td className="px-4 py-3 text-right text-orange-700 text-lg">${activitiesTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Activity Breakdown Summary */}
                  {activityBreakdown.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                      <h4 className="text-sm font-bold text-yellow-900 mb-3">Activity Breakdown:</h4>
                      <div className="space-y-2">
                        {activityBreakdown.map((activity, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm border-b border-yellow-200 pb-2">
                            <span className="text-gray-700">{activity.name}</span>
                            <span className="font-bold text-yellow-900">${activity.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No activities payments in this period</p>
              )}
            </div>

            {/* Daycare Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-purple-100 px-4 py-2 rounded">Daycare Payments</h3>
              {daycarePayments.length > 0 ? (
                <div>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daycarePayments.map(payment => (
                        <tr key={payment.PaymentID} className="border-b border-gray-200">
                          <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                          <td className="px-4 py-2 text-sm">{payment.FamilyID} - {getFamilyName(payment.FamilyID)}</td>
                          <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-purple-50 font-bold border-t-2 border-purple-300">
                        <td colSpan="4" className="px-4 py-3 text-right">Daycare Total:</td>
                        <td className="px-4 py-3 text-right text-purple-700 text-lg">${daycareTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Month Breakdown Summary */}
                  {daycareMonthBreakdown.length > 0 && (
                    <div className="mt-4 p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
                      <h4 className="text-sm font-bold text-purple-900 mb-3">Month Breakdown:</h4>
                      <div className="space-y-2">
                        {daycareMonthBreakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm border-b border-purple-200 pb-2">
                            <span className="text-gray-700">{item.month}</span>
                            <span className="font-bold text-purple-900">${item.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No daycare payments in this period</p>
              )}
            </div>

            {/* Art School Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-indigo-100 px-4 py-2 rounded">Art School Payments</h3>
              {artSchoolPayments.length > 0 ? (
                <div>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {artSchoolPayments.map(payment => (
                        <tr key={payment.PaymentID} className="border-b border-gray-200">
                          <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                          <td className="px-4 py-2 text-sm">{payment.FamilyID} - {getFamilyName(payment.FamilyID)}</td>
                          <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                          <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-indigo-50 font-bold border-t-2 border-indigo-300">
                        <td colSpan="4" className="px-4 py-3 text-right">Art School Total:</td>
                        <td className="px-4 py-3 text-right text-indigo-700 text-lg">${artSchoolTotal.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Class Breakdown */}
                  {artSchoolBreakdown.length > 0 && (
                    <div className="mt-4 p-4 bg-indigo-50 border-2 border-indigo-300 rounded-lg">
                      <h4 className="text-sm font-bold text-indigo-900 mb-3">Class Breakdown:</h4>
                      <div className="space-y-2">
                        {artSchoolBreakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-sm border-b border-indigo-200 pb-2">
                            <span className="text-gray-700">{item.name}</span>
                            <span className="font-bold text-indigo-900">${item.total.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No art school payments in this period</p>
              )}
            </div>

            {/* Bus Rental Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-purple-100 px-4 py-2 rounded">Bus Rental Payments</h3>
              {busRentalPayments.length > 0 ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Group/Destination</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Method</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {busRentalPayments.map(payment => (
                      <tr key={payment.PaymentID} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-sm">{new Date(payment.PaymentDate).toLocaleDateString()}</td>
                        <td className="px-4 py-2 text-sm">#{payment.PaymentID}</td>
                        <td className="px-4 py-2 text-sm">{payment.Notes || payment.ReferenceNumber}</td>
                        <td className="px-4 py-2 text-sm">{payment.PaymentMethod}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold">${payment.Amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-purple-50 font-bold border-t-2 border-purple-300">
                      <td colSpan="4" className="px-4 py-3 text-right">Bus Rental Total:</td>
                      <td className="px-4 py-3 text-right text-purple-700 text-lg">${busRentalTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No bus rental payments in this period</p>
              )}
            </div>

            {/* Store Section */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3 bg-teal-100 px-4 py-2 rounded">Store (Uniforms &amp; Books)</h3>
              {storeReceipts.length > 0 ? (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 border-b-2 border-gray-300">
                      <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Receipt #</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold">Family</th>
                      <th className="px-4 py-2 text-center text-sm font-semibold">Items</th>
                      <th className="px-4 py-2 text-right text-sm font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeReceipts.map(r => (
                      <tr key={r.OrderID} className="border-b border-gray-200">
                        <td className="px-4 py-2 text-sm">{r.PaidAt ? new Date(r.PaidAt).toLocaleDateString() : ''}</td>
                        <td className="px-4 py-2 text-sm">#{r.ReceiptNumber}</td>
                        <td className="px-4 py-2 text-sm">{r.FamilyID} - {r.FamilyName || r.FamilyID}</td>
                        <td className="px-4 py-2 text-sm text-center">{r.ItemCount}</td>
                        <td className="px-4 py-2 text-sm text-right font-semibold">${Number(r.AmountPaid).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-teal-50 font-bold border-t-2 border-teal-300">
                      <td colSpan="4" className="px-4 py-3 text-right">Store Total:</td>
                      <td className="px-4 py-3 text-right text-teal-700 text-lg">${storeTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p className="text-gray-500 italic text-center py-4">No store sales in this period</p>
              )}

              {/* Category Breakdown (Clothes / Books / …) */}
              {storeBreakdown.length > 0 && (
                <div className="mt-4 p-4 bg-teal-50 border-2 border-teal-300 rounded-lg">
                  <h4 className="text-sm font-bold text-teal-900 mb-3">Category Breakdown:</h4>
                  <div className="space-y-2">
                    {storeBreakdown.map((c, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm border-b border-teal-200 pb-2">
                        <span className="text-gray-700">{c.category}</span>
                        <span className="font-bold text-teal-900">${Number(c.total).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Grand Total */}
            <div className="mt-8 p-6 bg-gray-100 rounded-lg border-2 border-gray-400">
              <h3 className="text-xl font-bold text-center mb-4 text-gray-800">Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Afterschool:</span>
                  <span className="font-semibold text-green-700">${afterschoolTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Bus:</span>
                  <span className="font-semibold text-blue-700">${busTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Activities:</span>
                  <span className="font-semibold text-orange-700">${activitiesTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Daycare:</span>
                  <span className="font-semibold text-purple-700">${daycareTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Art School:</span>
                  <span className="font-semibold text-indigo-700">${artSchoolTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Bus Rental:</span>
                  <span className="font-semibold text-purple-700">${busRentalTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base">
                  <span className="text-gray-700">Store:</span>
                  <span className="font-semibold text-teal-700">${storeTotal.toFixed(2)}</span>
                </div>
                <div className="border-t-2 border-gray-400 pt-2 mt-2">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-gray-900">GRAND TOTAL:</span>
                    <span className="text-gray-900">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyReportModal;